// Transactions — tenant-wide audit log.
//
// Replaced the prior cash-only "cash book" view (closing balance + 7 buckets)
// with a flat, filterable, paginated stream that unions every payment recorded
// against a subscription ledger row with every manually-added cash-book entry
// (opening balance, cash payouts, bank deposits/withdrawals, expenses,
// adjustments). One screen, one mental model: "everything that happened to
// money in this tenant, newest first".
//
// Filters: payment method chips (UPI / Cash / Cheque / NEFT / Bank transfer)
// and cash-book kind chips (Opening / Payout / Bank in/out / Expense /
// Adjustment), date range, and a free-text search across name / phone /
// reference / note. Filters apply server-side so pagination stays consistent.
//
// Manual entries (expense, adjustment, etc.) are still recorded via the
// existing CashEntryModal, opened from the "+ Add entry" button — those land
// in the same stream because the backend union'd both sources.

import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from '@tanstack/react-query';
import { format_inr } from '../lib/format.js';
import { orpc } from '../lib/orpc.js';
import { friendly_error } from '../lib/friendly-error.js';
import { current_language } from '../i18n/index.js';

type CashbookKind =
  | 'opening_balance'
  | 'cash_in_payment'
  | 'cash_out_payout'
  | 'bank_deposit'
  | 'bank_withdrawal'
  | 'expense'
  | 'adjustment';

type PaymentMethod = 'upi' | 'cash' | 'cheque' | 'neft' | 'bank_transfer';

interface Transaction {
  id: string;
  source: 'collection' | 'cashbook';
  timestamp: string;
  kind: string;
  method: PaymentMethod | null;
  amount_paise: number;
  subscriber_id: string | null;
  subscriber_name_en: string | null;
  subscriber_name_ta: string | null;
  subscriber_phone: string | null;
  chit_id: string | null;
  chit_name_en: string | null;
  chit_name_ta: string | null;
  due_month: string | null;
  reference: string | null;
  note: string | null;
}

// Filter discriminator drives both the chip rail UI and the server filter.
// "all" sends no method/kind; method values restrict to collection rows;
// kind values restrict to cashbook rows.
type Filter =
  | { tag: 'all' }
  | { tag: 'method'; value: PaymentMethod }
  | { tag: 'kind'; value: CashbookKind };

const METHOD_LABEL: Record<PaymentMethod, string> = {
  upi: 'UPI',
  cash: 'Cash',
  cheque: 'Cheque',
  neft: 'NEFT',
  bank_transfer: 'Bank transfer',
};

const KIND_LABEL: Record<CashbookKind, string> = {
  opening_balance: 'Opening balance',
  cash_in_payment: 'Cash collection',
  cash_out_payout: 'Cash payout',
  bank_deposit: 'Bank deposit',
  bank_withdrawal: 'Bank withdrawal',
  expense: 'Expense',
  adjustment: 'Adjustment',
};

function today_ymd(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

function default_from(): string {
  // Default window: last 30 days. Most operators look at "this month-ish"
  // for daily reconciliation; the date picker lets them widen.
  const d = new Date(Date.now() - 30 * 86_400_000);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

export function TransactionsPage(): ReactElement {
  const { t } = useTranslation();
  const lang = current_language();
  const [filter, set_filter] = useState<Filter>({ tag: 'all' });
  const [from, set_from] = useState(default_from());
  const [to, set_to] = useState(today_ymd());
  const [search, set_search] = useState('');

  const PAGE_SIZE = 25;
  const q = useInfiniteQuery({
    queryKey: [
      'transactions',
      { method: filter.tag === 'method' ? filter.value : null,
        kind: filter.tag === 'kind' ? filter.value : null,
        from, to, search: search.trim() },
    ],
    queryFn: ({ pageParam }) =>
      orpc.cashbook.list_all_transactions({
        ...(filter.tag === 'method' ? { method: filter.value } : {}),
        ...(filter.tag === 'kind' ? { kind: filter.value } : {}),
        from,
        to,
        ...(search.trim() ? { search: search.trim() } : {}),
        offset: pageParam,
        limit: PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => (last.has_more ? pages.length * PAGE_SIZE : undefined),
  });

  const all = (q.data?.pages.flatMap((p) => p.transactions ?? []) ?? []) as Transaction[];
  const total = q.data?.pages[0]?.total_count ?? 0;
  const loaded = all.length;

  // Inflow / outflow tallies for the filtered window — purely for the
  // header strip; not the closing-balance computation the old page did.
  const inflow = all.filter((t) => t.amount_paise > 0).reduce((s, t) => s + t.amount_paise, 0);
  const outflow = all
    .filter((t) => t.amount_paise < 0)
    .reduce((s, t) => s + Math.abs(t.amount_paise), 0);

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-2xl font-medium">
          {t('transactions.title', 'Transactions')}
        </h1>
        <p className="text-xs text-ink/60 mt-1" style={{ maxWidth: 720, lineHeight: 1.5 }}>
          {t(
            'transactions.subtitle',
            'Every payment collected across all chits. Filter by method, date range, or search.',
          )}
        </p>
      </div>

      {/* Window summary — inflow / outflow / net for the loaded set */}
      <div
        className="bg-surface border border-line rounded-lg p-4 mb-4"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14 }}
      >
        <Tile label={t('transactions.window', 'Window')} value={`${from} → ${to}`} mono={false} />
        <Tile
          label={t('transactions.inflow', 'Inflow')}
          value={format_inr(inflow)}
          tone="good"
        />
        <Tile
          label={t('transactions.outflow', 'Outflow')}
          value={format_inr(outflow)}
          tone="bad"
        />
        <Tile
          label={t('transactions.net', 'Net')}
          value={(inflow - outflow >= 0 ? '+' : '−') + format_inr(Math.abs(inflow - outflow))}
          tone={inflow - outflow >= 0 ? 'good' : 'bad'}
        />
        <Tile
          label={t('transactions.shown', 'Shown')}
          value={`${loaded} / ${total}`}
          mono
        />
      </div>

      {/* Filter rail — payment-method chips, then kind chips, then date+search */}
      <div className="bg-surface border border-line rounded-lg p-3 mb-4">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 8 }}>
          <span className="text-[10px] uppercase tracking-wider text-ink/50 mr-1">
            {t('transactions.method', 'Method')}
          </span>
          <FilterChip on={filter.tag === 'all'} on_click={() => set_filter({ tag: 'all' })}>
            {t('transactions.all', 'All')}
          </FilterChip>
          {(Object.keys(METHOD_LABEL) as PaymentMethod[]).map((m) => (
            <FilterChip
              key={m}
              on={filter.tag === 'method' && filter.value === m}
              on_click={() => set_filter({ tag: 'method', value: m })}
            >
              {METHOD_LABEL[m]}
            </FilterChip>
          ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 10 }}>
          <span className="text-[10px] uppercase tracking-wider text-ink/50 mr-1">
            {t('transactions.cashbook_kinds', 'Manual entries')}
          </span>
          {(Object.keys(KIND_LABEL) as CashbookKind[])
            .filter((k) => k !== 'cash_in_payment') // duplicate of method=cash collection
            .map((k) => (
              <FilterChip
                key={k}
                on={filter.tag === 'kind' && filter.value === k}
                on_click={() => set_filter({ tag: 'kind', value: k })}
              >
                {KIND_LABEL[k]}
              </FilterChip>
            ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <input
            type="date"
            value={from}
            onChange={(e) => set_from(e.target.value)}
            className="border border-line bg-surface px-2 py-1 rounded text-sm font-mono"
          />
          <span className="text-ink/40">→</span>
          <input
            type="date"
            value={to}
            onChange={(e) => set_to(e.target.value)}
            className="border border-line bg-surface px-2 py-1 rounded text-sm font-mono"
          />
          <div style={{ flex: 1 }} />
          <input
            type="text"
            value={search}
            onChange={(e) => set_search(e.target.value)}
            placeholder={t('transactions.search', 'Search name, phone, reference, note…')}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--line)',
              borderRadius: 6,
              background: 'var(--surface)',
              fontSize: 12,
              minWidth: 260,
            }}
          />
        </div>
      </div>

      {/* Transactions table */}
      <div className="bg-surface border border-line rounded-lg overflow-hidden">
        {q.isLoading && <div className="px-5 py-6 text-sm text-ink/60">Loading…</div>}
        {q.error && <div className="px-5 py-4 text-sm text-bad">{friendly_error(q.error)}</div>}
        {q.data && all.length === 0 && (
          <div className="px-5 py-10 text-center text-sm text-ink/60">
            {t('transactions.empty', 'No transactions match the current filters')}
          </div>
        )}
        {all.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-bg-elev border-b border-line text-ink/60 text-xs">
              <tr>
                <th className="text-left px-4 py-2 font-medium" style={{ width: 130 }}>
                  {t('transactions.when', 'When')}
                </th>
                <th className="text-left px-4 py-2 font-medium" style={{ width: 130 }}>
                  {t('transactions.type', 'Type')}
                </th>
                <th className="text-left px-4 py-2 font-medium">
                  {t('transactions.who', 'Subscriber / Note')}
                </th>
                <th className="text-left px-4 py-2 font-medium">
                  {t('transactions.where', 'Chit / Month')}
                </th>
                <th className="text-left px-4 py-2 font-medium">
                  {t('transactions.ref', 'Reference')}
                </th>
                <th className="text-right px-4 py-2 font-medium" style={{ width: 130 }}>
                  {t('transactions.amount', 'Amount')}
                </th>
              </tr>
            </thead>
            <tbody>
              {all.map((tx) => (
                <Row key={tx.id} tx={tx} lang={lang} />
              ))}
            </tbody>
          </table>
        )}

        {q.hasNextPage && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              padding: '12px 14px',
              borderTop: '1px solid var(--line)',
              background: 'var(--bg-elev)',
              fontSize: 12,
              color: 'var(--ink-3)',
            }}
          >
            <span>
              {t('transactions.showing', 'Showing')}{' '}
              <span className="mono" style={{ color: 'var(--ink)', fontWeight: 600 }}>
                {loaded}
              </span>{' '}
              {t('transactions.of', 'of')}{' '}
              <span className="mono">{total}</span>
            </span>
            <button
              className="btn sm"
              disabled={q.isFetchingNextPage}
              onClick={() => q.fetchNextPage()}
            >
              {q.isFetchingNextPage
                ? t('transactions.loading', 'Loading…')
                : `${t('transactions.show_more', 'Show more')} (+${PAGE_SIZE})`}
            </button>
          </div>
        )}
        {!q.hasNextPage && loaded > 0 && loaded === total && (
          <div
            style={{
              padding: '10px 14px',
              borderTop: '1px solid var(--line)',
              background: 'var(--bg-elev)',
              fontSize: 11,
              color: 'var(--ink-3)',
              textAlign: 'center',
            }}
          >
            {t('transactions.all_loaded', 'All loaded')}
          </div>
        )}
      </div>

    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function FilterChip({
  on,
  on_click,
  children,
}: {
  on: boolean;
  on_click: () => void;
  children: React.ReactNode;
}): ReactElement {
  return (
    <button
      onClick={on_click}
      className={'chip' + (on ? ' on' : '')}
      style={{ fontSize: 12 }}
    >
      {children}
    </button>
  );
}

function Tile({
  label,
  value,
  tone,
  mono = true,
}: {
  label: string;
  value: string;
  tone?: 'good' | 'bad';
  mono?: boolean;
}): ReactElement {
  const color = tone === 'good' ? 'var(--good)' : tone === 'bad' ? 'var(--bad)' : 'var(--ink)';
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-ink/50 mb-1">{label}</div>
      <div
        className={mono ? 'font-mono' : ''}
        style={{ fontSize: 16, fontWeight: 600, color }}
      >
        {value}
      </div>
    </div>
  );
}

function Row({ tx, lang }: { tx: Transaction; lang: string }): ReactElement {
  const when = new Date(tx.timestamp);
  const is_outflow = tx.amount_paise < 0;
  const type_label =
    tx.source === 'collection'
      ? METHOD_LABEL[(tx.method ?? 'upi') as PaymentMethod]
      : (KIND_LABEL[tx.kind as CashbookKind] ?? tx.kind);
  const who =
    tx.source === 'collection'
      ? (lang === 'ta' ? tx.subscriber_name_ta : tx.subscriber_name_en) ?? '—'
      : (tx.note ?? '—');
  const where =
    tx.source === 'collection'
      ? `${(lang === 'ta' ? tx.chit_name_ta : tx.chit_name_en) ?? '—'} · ${tx.due_month ?? '—'}`
      : '—';
  return (
    <tr className="border-b border-line/40 last:border-b-0">
      <td className="px-4 py-2 font-mono text-xs">
        <div>
          {when.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
        </div>
        <div style={{ color: 'var(--ink-3)' }}>
          {when.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
        </div>
      </td>
      <td className="px-4 py-2">
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 999,
            background: tx.source === 'collection' ? 'var(--good-soft)' : 'var(--bg-elev)',
            color: tx.source === 'collection' ? 'var(--good)' : 'var(--ink)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          {type_label}
        </span>
      </td>
      <td className="px-4 py-2">
        <div className={lang === 'ta' ? 'tamil' : ''}>{who}</div>
        {tx.subscriber_phone && (
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{tx.subscriber_phone}</div>
        )}
      </td>
      <td className="px-4 py-2 text-xs" style={{ color: 'var(--ink-3)' }}>
        {where}
      </td>
      <td className="px-4 py-2 font-mono text-xs" style={{ color: 'var(--ink-3)' }}>
        {tx.reference ?? '—'}
      </td>
      <td
        className="px-4 py-2 text-right font-mono"
        style={{
          fontWeight: 700,
          color: is_outflow ? 'var(--bad)' : 'var(--good)',
        }}
      >
        {is_outflow ? '−' : '+'}
        {format_inr(Math.abs(tx.amount_paise))}
      </td>
    </tr>
  );
}

// Manual cashbook entry modal removed — the Transactions page is now a
// read-only audit log. The backend procs (cashbook.create_entry +
// transactions.list_all unioning cash_book) still work, so a future
// admin-tools page could surface manual entry without us touching the
// chit-domain handler.
